import type { QueryClient } from '@tanstack/react-query'
import { QueryClientProvider } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { SITE_DEFAULT_DESCRIPTION, SITE_DEFAULT_TITLE } from '@/constants/site'
import { buildPageMeta } from '@/lib/seo/build-page-meta'
import { buildFloristJsonLd } from '@/lib/seo/json-ld'
import globalsCss from '@/styles/globals.css?url'

interface FloraRouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<FloraRouterContext>()({
  head: () => {
    const { meta } = buildPageMeta({
      title: SITE_DEFAULT_TITLE,
      description: SITE_DEFAULT_DESCRIPTION,
      path: '/',
    })

    return {
      meta: [
        { charSet: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ...meta,
      ],
      links: [
        {
          rel: 'icon',
          type: 'image/svg+xml',
          href: '/favicon.svg',
        },
        {
          rel: 'preload',
          as: 'font',
          type: 'font/woff2',
          href: '/fonts/fraunces-variable.woff2',
          crossOrigin: 'anonymous',
        },
        {
          rel: 'preload',
          as: 'font',
          type: 'font/woff2',
          href: '/fonts/fraunces-italic-variable.woff2',
          crossOrigin: 'anonymous',
        },
        {
          rel: 'preload',
          as: 'font',
          type: 'font/woff2',
          href: '/fonts/instrument-sans-variable.woff2',
          crossOrigin: 'anonymous',
        },
        { rel: 'stylesheet', href: globalsCss },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFloristJsonLd()),
        },
      ],
    }
  },
  component: RootComponent,
})

function RootComponent() {
  const { queryClient } = Route.useRouteContext()

  return (
    <QueryClientProvider client={queryClient}>
      <RootDocument>
        <Outlet />
      </RootDocument>
    </QueryClientProvider>
  )
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  )
}
